// Live chat — the CRM half of the FOS → human handoff.
//
// A staffer flips "Available for chat" in the dock; while they are, this tab
// heartbeats presence and polls the inbox. A shopper who asks the assistant for
// a person lands in everyone's queue; whoever accepts talks to them inside the
// shopper's own chat window on squish.co. See squish-backend lib/live-chat.js
// for the state machine and squish-crm-backend server.js for identity (the
// CRM resolves WHO from the Cloudflare Access session — nothing here names
// the staffer).
//
// Pure helpers live beside the fetchers so the rules can be tested without a
// browser (test/live-chat.test.js).

import { getHeaders } from '../api';

const BASE = import.meta.env?.PROD ? '' : 'https://crm-api.squish.co';

// Heartbeat cadence. Backend treats a staffer as gone after 60s without one,
// so three missed beats drop them — a closed laptop stops receiving chats
// within a minute.
export const HEARTBEAT_MS = 20_000;
// Inbox poll while available. Fast enough that a waiting shopper is seen in a
// couple of seconds; one small read per poll.
export const INBOX_POLL_MS = 3_000;
// Open-thread poll. A live conversation should feel live.
export const THREAD_POLL_MS = 2_000;

export const AVAILABLE_KEY = 'crm_live_chat_available';

async function req(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: getHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty */ }
  if (!res.ok) {
    const err = new Error(data?.message || data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.error || null;
    throw err;
  }
  return data;
}

export const liveChatApi = {
  presence: (status, channels = ['chat']) => req('POST', '/api/live-chat/presence', { status, channels }),
  inbox: () => req('GET', '/api/live-chat/inbox'),
  session: (id, after = 0) => req('GET', `/api/live-chat/sessions/${encodeURIComponent(id)}?after=${Number(after) || 0}`),
  accept: (id) => req('POST', `/api/live-chat/sessions/${encodeURIComponent(id)}/accept`),
  send: (id, text) => req('POST', `/api/live-chat/sessions/${encodeURIComponent(id)}/messages`, { text }),
  end: (id) => req('POST', `/api/live-chat/sessions/${encodeURIComponent(id)}/end`),
  // What the chat's Case is about — only the fields that changed.
  editCase: (id, fields) => req('POST', `/api/live-chat/sessions/${encodeURIComponent(id)}/case`, fields),
  // Fired from `pagehide`. keepalive lets the request outlive the page; the
  // response is never read.
  awayOnUnload: () => {
    try {
      fetch(`${BASE}/api/live-chat/presence`, {
        method: 'POST', keepalive: true, headers: getHeaders(),
        body: JSON.stringify({ status: 'away', channels: ['chat'] }),
      });
    } catch { /* best effort */ }
  },
};

export * from './liveChatRules';

export function readAvailable() {
  try { return localStorage.getItem(AVAILABLE_KEY) === '1'; } catch { return false; }
}

export function writeAvailable(on) {
  try { localStorage.setItem(AVAILABLE_KEY, on ? '1' : '0'); } catch { /* private mode */ }
}
